import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDataGridTextFilter } from '../../../../../common/DataGrid/filters/useDataGridTextFilter.jsx';
import {
  dataGridDateSorter,
  dataGridTextSorter,
} from '../../../../../common/DataGrid/sorters/DataGridSorters.jsx';
import { QuestionModal, Spinner } from '../../../../../common/index.jsx';
import PropTypes from 'prop-types';
import DataGrid from '../../../../../common/DataGrid/DataGrid.jsx';
import { useDeleteInterestMutation } from '../../../../../../redux/services/fij_applications.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { IconButton } from '../../../../../common/controls/Controls.jsx';
import {
  EditOutlined,
  FileSearchOutlined,
  UserDeleteOutlined,
} from '@ant-design/icons';
import {
  FIJ_INTEREST_STATUS,
  statusColorInterest,
} from '../../../../../../utils/constants/enums.js';
import { useDataGridDateRangeFilter } from '../../../../../common/DataGrid/filters/useDataGridDateRangeFilter.jsx';
import { useStateFriPraxInterestsDataGridFilter } from '../../../../../common/DataGrid/filters/DataGridFilters.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages.jsx';
import { Tag } from 'antd';

const FijMyInterestsTable = ({ myInterests, refetchData, onDetailClick }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [rowToBeDeleted, setRowToBeDeleted] = useState(null);

  const { deleteSuccess } = useToastMessages();

  const nameTextFilterProps = useDataGridTextFilter('internal_job.name');
  const supervisorTextFilterProps = useDataGridTextFilter(
    'supervisor_full_name',
  );
  const statusTextFilterProps =
    useStateFriPraxInterestsDataGridFilter('status');
  const fromDateRangeFilterProps = useDataGridDateRangeFilter(
    'internal_job_date_from',
  );
  const toDateRangeFilterProps = useDataGridDateRangeFilter(
    'internal_job_date_to',
  );
  const acceptanceDateRangeFilterProps =
    useDataGridDateRangeFilter('date_acceptance');

  const myInterestsSorted = myInterests
    .slice()
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const [deleteInterest, { isSuccess: deletedInterest }] =
    useDeleteInterestMutation();

  const customSettings = (myInterest) => {
    return (
      <>
        {myInterest.status === FIJ_INTEREST_STATUS.WAITING && (
          <IconButton
            icon={<EditOutlined />}
            tooltip={t('Upraviť')}
            onClick={(e) => {
              e.stopPropagation();
              navigate(`${routes.cmsFijJobInterestsEdit}/${myInterest.id}`);
            }}
          />
        )}

        {myInterest.status === FIJ_INTEREST_STATUS.WAITING && (
          <IconButton
            icon={<UserDeleteOutlined />}
            tooltip={t('Odhlásiť sa z FRI praxe')}
            onClick={(e) => {
              e.stopPropagation();
              setRowToBeDeleted(myInterest.id);
            }}
          />
        )}

        <IconButton
          icon={<FileSearchOutlined />}
          tooltip={t('Zobraziť detail FRI praxe')}
          onClick={(e) => {
            e.stopPropagation();
            onDetailClick(myInterest.internal_job.id);
          }}
        />
      </>
    );
  };

  useEffect(() => {
    if (deletedInterest) {
      deleteSuccess();
      refetchData();
    }
  }, [deletedInterest]);

  const dataGridColumns = useMemo(
    () => [
      {
        title: t('Názov'),
        dataIndex: ['internal_job', 'name'],
        ellipsis: true,
        sorter: dataGridTextSorter(['internal_job', 'name']),
        ...nameTextFilterProps,
      },
      {
        title: t('Lektor'),
        dataIndex: 'supervisor_full_name',
        sorter: dataGridTextSorter('supervisor_full_name'),
        ...supervisorTextFilterProps,
      },
      {
        title: t('Začiatok od'),
        dataIndex: 'internal_job_date_from',
        ellipsis: true,
        sorter: dataGridDateSorter('internal_job_date_from'),
        render: (date) => (date && new Date(date).toLocaleDateString()) || '-',
        ...fromDateRangeFilterProps,
      },
      {
        title: t('Koniec do'),
        dataIndex: 'internal_job_date_to',
        ellipsis: true,
        sorter: dataGridDateSorter('internal_job_date_to'),
        render: (date) => (date && new Date(date).toLocaleDateString()) || '-',
        ...toDateRangeFilterProps,
      },
      {
        title: t('Stav'),
        dataIndex: 'status',
        ellipsis: true,
        sorter: dataGridTextSorter('status'),
        ...statusTextFilterProps,
        render: (status) => (
          <Tag color={statusColorInterest(status)}>{t(status)}</Tag>
        ),
      },
      {
        title: t('Dátum prijatia'),
        dataIndex: 'date_acceptance',
        ellipsis: true,
        sorter: dataGridDateSorter('date_acceptance'),
        render: (date) => (date && new Date(date).toLocaleDateString()) || '-',
        ...acceptanceDateRangeFilterProps,
      },
    ],
    [
      t,
      nameTextFilterProps,
      acceptanceDateRangeFilterProps,
      fromDateRangeFilterProps,
      statusTextFilterProps,
      supervisorTextFilterProps,
      toDateRangeFilterProps,
    ],
  );

  return !myInterests ? (
    <Spinner />
  ) : (
    <>
      <DataGrid
        rowKey={(myInterest) => myInterest.id}
        columns={dataGridColumns}
        dataSource={myInterestsSorted || []}
        permissionsBasic={'fri-internal-jobs-interests'}
        uniqueTableId={'fij-my-interests-table'}
        scroll={{ x: 'max-content' }}
        customSettings={customSettings}
        onRowClick={(record) => {
          if (
            record.status === FIJ_INTEREST_STATUS.WAITING &&
            new Date(record.internal_job_date_from) > new Date()
          ) {
            navigate(`${routes.cmsFijJobInterestsEdit}/${record.id}`);
          } else {
            onDetailClick(record.internal_job.id);
          }
        }}
        onRowDetailClick={(record) => onDetailClick(record.internal_job.id)}
      />

      <QuestionModal
        chosenPositive={() => {
          deleteInterest(rowToBeDeleted);
          setRowToBeDeleted(null);
        }}
        question={t('Naozaj sa chcete odhlásiť z FRI praxe?')}
        visible={rowToBeDeleted !== null}
        yes={t('Áno')}
        toggle={() => setRowToBeDeleted(null)}
        headerTitle={t('Odhlásiť sa z FRI praxe')}
      />
    </>
  );
};

FijMyInterestsTable.propTypes = {
  myInterests: PropTypes.any,
  onDelete: PropTypes.func,
  refetchData: PropTypes.func,
  onDetailClick: PropTypes.func,
};

export default FijMyInterestsTable;
